/**
 * Storefront v6 — products: the card, the popular strip, the menu browser
 * (search + category groups) and the product detail sheet.
 *
 * Pricing and the size picker stay on the ORDER PAGE (the one à-la-carte
 * money rail): a sized item hands off to /order's own picker, and the
 * storefront cart holds only item ids + quantities that /order re-prices.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Photo, Badge, Price, QuickAdd, InlineLink, SectionHead, Skeleton, OrderNowButton } from '../primitives';
import ProductGallery from '../../../storefront/ProductGallery';

// ── Item helpers (the order page's own rules) ───────────────────────────
export const itemPrice = (item) => {
  const p = Number(item?.price);
  if (p > 0) return p;
  const bp = Number(item?.base_price);
  if (bp > 0) return bp;
  const tiers = (item?.price_tiers || []).map((t) => Number(t?.price)).filter((n) => n > 0);
  return tiers.length ? Math.min(...tiers) : 0;
};
export const itemSizes = (item) => (Array.isArray(item?.size_options) ? item.size_options : []);
export const itemHasSizes = (item) => itemSizes(item).length > 1;
const slug = (s) => String(s || 'other').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'other';

/** Badges only from real data: the dietary tags / badges the item itself carries. */
const itemBadges = (item) => {
  const out = [];
  const tags = [...(Array.isArray(item?.dietary_tags) ? item.dietary_tags : []), ...(Array.isArray(item?.badges) ? item.badges : [])]
    .map((t) => String(t || '').toLowerCase().trim()).filter(Boolean);
  for (const t of tags.slice(0, 3)) {
    const kind = t.includes('vegan') ? 'vegan' : (t.includes('veg') ? 'veg' : t.includes('spic') ? 'spicy' : t === 'new' ? 'new' : null);
    out.push({ kind, label: t.charAt(0).toUpperCase() + t.slice(1) });
  }
  return out;
};

export const ProductCard = ({ item, currency, qty = 0, canOrder, reason, onAdd, onRemove, onOpen, ratio = '1 / 1', className = '' }) => {
  const price = itemPrice(item);
  // TWO REFUSALS, TWO TREATMENTS. `!!item.out_of_stock || (!canOrder && !!reason)` fused
  // them, so an off-window lunch dish wore a black "Sold out" badge on the card while
  // the sheet, three lines away, said it "isn't available at this time of day".
  // An 86'd dish is HIDDEN from the storefront upstream (Storefront.jsx `sellable`), so
  // this branch is reached only through the cart, which keeps a line it already holds.
  // Every other refusal is a dish the merchant still sells: full colour, and the reason
  // said out loud - including when the feed gave no words for it, which used to render
  // a normal-looking card with no Add button and nothing to explain why.
  const soldOut = !!item.out_of_stock;
  const unavailable = !soldOut && !canOrder;
  const badges = itemBadges(item);
  return (
    <article className={`s6-card s6-card-hover overflow-hidden flex flex-col ${className}`} data-testid={`s6-item-${item.id}`}>
      <button type="button" onClick={() => onOpen(item)} className="text-left" aria-label={`${item.name}, details`}>
        <Photo src={item.image_url} alt={item.name} ratio={ratio} soldOut={soldOut} className="!rounded-none">
          {soldOut && <span className="absolute top-3 left-3"><Badge kind="soldout">Sold out</Badge></span>}
          {item.image_source === 'ai' && <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/55 text-white" data-testid="storefront-ai-image-tag">AI generated image</span>}
        </Photo>
      </button>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="s6-h3 leading-tight">{item.name}</h3>
            {item.description && <p className="mt-1 text-[13px] leading-snug line-clamp-2" style={{ color: 'var(--s6-ink-muted)' }}>{item.description}</p>}
          </div>
        </div>
        <div className="mt-auto flex items-end justify-between gap-2">
          <div>
            {price > 0 && <Price amount={price} currency={currency} from={itemHasSizes(item)} className="text-[15px]" />}
            {badges.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {badges.map((b) => <Badge key={b.label} kind={b.kind}>{b.label}</Badge>)}
              </div>
            )}
          </div>
          {canOrder && !soldOut && (
            <QuickAdd qty={qty} label={item.name} onAdd={() => onAdd(item)} onRemove={() => onRemove(item)} testid={`s6-add-${item.id}`} testidStepper={`s6-qty-${item.id}`} />
          )}
        </div>
        {unavailable && <p className="text-xs" style={{ color: 'var(--s6-warn)' }} data-testid={`s6-unavailable-${item.id}`}>{reason || 'Not available to order right now.'}</p>}
      </div>
    </article>
  );
};

/** A horizontal strip of photo-first cards with arrows and one link. */
export const PopularDishes = ({ items = [], copy = {}, theme, onSeeAll, cardProps, minimumItems = 3 }) => {
  const ref = useRef(null);
  const scroll = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * Math.max(280, Math.round(el.clientWidth * 0.8)), behavior: 'smooth' }); };
  if (items.length < minimumItems) return null;
  return (
    <section id="popular" className="s6-section s6-surface-white" data-testid="s6-popular">
      <div className="s6-container">
        <div className="flex items-end justify-between gap-6 mb-6 md:mb-8">
          <div>
            {copy.eyebrow && <span className="s6-eyebrow">{copy.eyebrow}</span>}
            <h2 className="s6-h2">{copy.title}{copy.emphasis ? ' ' : ''}{copy.emphasis && <span className={theme.accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'}>{copy.emphasis}</span>}</h2>
          </div>
          {onSeeAll && <InlineLink onClick={onSeeAll} data-testid="s6-popular-all">See all</InlineLink>}
        </div>
        <div ref={ref} className="s6-strip -mx-5 px-5 sm:-mx-6 sm:px-6 md:mx-0 md:px-0" data-testid="s6-popular-strip">
          {items.map((it) => (
            <ProductCard key={it.id} item={it} ratio="4 / 5" className="w-[236px] sm:w-[260px] md:w-[280px]" {...cardProps(it)} />
          ))}
        </div>
        <div className="mt-5 hidden md:flex justify-center gap-2">
          <button type="button" onClick={() => scroll(-1)} className="s6-icon-btn border" style={{ borderColor: 'var(--s6-line)' }} aria-label="Previous dishes"><ChevronLeft className="w-5 h-5" /></button>
          <button type="button" onClick={() => scroll(1)} className="s6-icon-btn border" style={{ borderColor: 'var(--s6-line)' }} aria-label="More dishes"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
    </section>
  );
};

/** Group the display feed by category, in the feed's category order. */
export const groupByCategory = (items = [], categoryOrder = []) => {
  const groups = new Map();
  for (const it of items) {
    const key = slug(it.category);
    if (!groups.has(key)) groups.set(key, { key, label: it.category || 'Other', items: [] });
    groups.get(key).items.push(it);
  }
  const rank = new Map((categoryOrder || []).map((c, i) => [slug(c), i]));
  return [...groups.values()].sort((a, b) => (rank.has(a.key) ? rank.get(a.key) : 999) - (rank.has(b.key) ? rank.get(b.key) : 999));
};

export const MenuBrowser = ({ groups = [], currency, loading, searchRef, cardProps, copy = {}, theme, onOrder, orderLabel, orderableCount = 0 }) => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => (q
    ? groups.map((g) => ({ ...g, items: g.items.filter((it) => `${it.name} ${it.description || ''} ${it.category || ''}`.toLowerCase().includes(q)) })).filter((g) => g.items.length)
    : groups), [groups, q]);
  const total = visible.reduce((n, g) => n + g.items.length, 0);
  return (
    <section id="menu" className="s6-section" data-testid="s6-menu">
      <div className="s6-container">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title || 'Our menu'} emphasis={copy.emphasis} sub={copy.sub} accentStyle={theme.accentStyle} />
        <div className="s6-input-pill max-w-xl mx-auto mb-8" data-testid="s6-menu-search">
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--s6-ink-muted)' }} aria-hidden="true" />
          <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search dishes" aria-label="Search dishes" className="py-2" />
          {query && <button type="button" onClick={() => setQuery('')} className="s6-icon-btn !w-9 !h-9" aria-label="Clear search"><X className="w-4 h-4" /></button>}
        </div>
        {visible.length > 1 && <label className="block max-w-xl mx-auto mb-8 text-sm">
          Jump to a category
          <select aria-label="Jump to a category" value="" className="block w-full mt-2 border rounded-xl p-3 bg-white" onChange={(e) => document.getElementById(`cat-${e.target.value}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}>
            <option value="" disabled>Choose a category</option>
            {visible.map((g) => <option key={g.key} value={g.key}>{g.label} ({g.items.length})</option>)}
          </select>
        </label>}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} ratio="1 / 1" />)}</div>
        )}
        {!loading && total === 0 && (
          <p className="text-center py-10" style={{ color: 'var(--s6-ink-muted)' }} data-testid="s6-menu-empty">
            {q ? `Nothing matches “${query}”.` : 'The menu is being updated. Please check back soon.'}
          </p>
        )}
        {visible.map((g) => (
          <div key={g.key} id={`cat-${g.key}`} className="mb-10 scroll-mt-32" data-testid={`s6-menu-group-${g.key}`}>
            <h3 className="s6-h3 !text-xl mb-4 flex items-baseline gap-2">{g.label} <span className="text-sm font-medium" style={{ color: 'var(--s6-ink-muted)' }}>{g.items.length}</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {g.items.map((it) => <ProductCard key={it.id} item={it} currency={currency} {...cardProps(it)} />)}
            </div>
          </div>
        ))}
        {orderableCount > 0 && onOrder && (
          <div className="mt-6 text-center">
            <OrderNowButton onClick={onOrder} data-testid="s6-menu-order">{orderLabel}</OrderNowButton>
          </div>
        )}
      </div>
    </section>
  );
};

/** The product detail: photo, description, price or sizes, add-on groups (named, priced), add. */
export const ProductSheet = ({ item, currency, qty = 0, canOrder, reason, onAdd, onRemove, onClose, onChooseSize, onDetails }) => {
  useEffect(() => {
    if (!item) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [item, onClose]);
  if (!item) return null;
  const sizes = itemSizes(item);
  const price = itemPrice(item);
  const soldOut = !!item.out_of_stock;
  const unavailable = !soldOut && !canOrder;
  const groups = Array.isArray(item.addon_groups) ? item.addon_groups : [];
  return (
    <>
      <div className="s6-backdrop" onClick={onClose} />
      <div className="s6-sheet" role="dialog" aria-modal="true" aria-label={item.name} data-testid="s6-product-sheet">
        <button type="button" onClick={onClose} className="s6-icon-btn absolute top-3 right-3 z-10 bg-white/80" aria-label="Close" data-testid="s6-product-close"><X className="w-5 h-5" /></button>
        <div className="s6-sheet-body">
          {item.images?.length > 0 ? <ProductGallery item={item} /> : <Photo src={item.image_url} alt={item.name} ratio="4 / 3" eager soldOut={soldOut} sizes="(min-width:768px) 640px, 100vw" className="!rounded-none" />}
          <div className="p-5 space-y-4">
            <div>
              <h2 className="s6-h2 !text-2xl">{item.name}</h2>
              {item.description && <p className="mt-2 s6-lede !text-base">{item.description}</p>}
              {onDetails && <button type="button" className="mt-3 text-sm underline underline-offset-4" onClick={() => onDetails(item)}>Ingredients, nutrition & preparation</button>}
            </div>
            {sizes.length > 1 ? (
              <ul className="s6-rows" data-testid="s6-product-sizes">
                {sizes.map((s) => (
                  <li key={s.key || s.label || s.name} className="flex justify-between py-2 text-sm">
                    <span>{s.label || s.name || s.key}</span>
                    <Price amount={price + Number(s.price_delta || 0)} currency={currency} />
                  </li>
                ))}
              </ul>
            ) : (price > 0 && <Price amount={price} currency={currency} className="text-xl" />)}
            {groups.length > 0 && (
              <div className="space-y-3" data-testid="s6-product-addons">
                {groups.map((g) => (
                  <div key={g.key || g.name}>
                    <p className="text-sm font-semibold">{g.name || g.label}{g.min_select > 0 && <span className="ml-2 font-medium" style={{ color: 'var(--s6-ink-muted)' }}>required</span>}</p>
                    <ul className="mt-1 text-sm" style={{ color: 'var(--s6-ink-muted)' }}>
                      {(g.options || []).map((o) => <li key={o.key} className="flex justify-between py-0.5"><span>{o.label}</span>{Number(o.price) > 0 && <Price amount={o.price} currency={currency} />}</li>)}
                    </ul>
                  </div>
                ))}
                <p className="text-xs" style={{ color: 'var(--s6-ink-muted)' }}>Choose your extras on the order page.</p>
              </div>
            )}
          </div>
        </div>
        <div className="s6-sheet-foot flex items-center justify-between gap-4">
          {soldOut ? (
            <span className="text-sm font-semibold" style={{ color: 'var(--s6-closed)' }} data-testid="s6-product-soldout">Sold out</span>
          ) : unavailable ? (
            <span className="text-sm font-semibold" style={{ color: 'var(--s6-warn)' }} data-testid="s6-product-unavailable">{reason || 'Not available to order right now.'}</span>
          ) : sizes.length > 1 ? (
            <OrderNowButton className="w-full" onClick={() => onChooseSize(item)} data-testid="s6-product-choose-size">Choose a size</OrderNowButton>
          ) : (
            <>
              <QuickAdd qty={qty} label={item.name} onAdd={() => onAdd(item)} onRemove={() => onRemove(item)} testid="s6-product-add" testidStepper="s6-product-qty" />
              <OrderNowButton onClick={() => { if (!qty) onAdd(item); onClose(); }} data-testid="s6-product-done">{qty ? 'Done' : 'Add to order'}</OrderNowButton>
            </>
          )}
        </div>
      </div>
    </>
  );
};
